import * as THREE from 'three';
import { defaultPose, type FaunaDefinition, type FaunaPose } from './FaunaDefinition';
import { instanceScale, type FaunaInstance } from './FaunaInstance';
import { buildFaunaGeometry } from './FaunaGeometry';

/**
 * Builds the Three.js view of a placed creature, mirroring `FloraFactory` and `SiteObjectWorld`'s
 * builder: the returned object is a **view**, carrying `userData.faunaInstanceId` back to the domain
 * entity, rebuilt whenever the instance changes.
 *
 * Two named parts, `body` and `accent`, coloured from the record's two colours. The split is what
 * makes a 90 mm silhouette identifiable — a robin is a grey-brown lump plus a rust breast, and
 * without the second colour it is just the lump.
 *
 * ## Materials are flat-shaded and vertex-coloured
 *
 * The same treatment procedural buildings get, and for the same reason: these are massing. A
 * smooth-shaded creature next to a flat-shaded shed reads as an import that did not belong, which is
 * exactly the failure `docs/PROPS.md` describes for photoscanned props. When authored meshes arrive
 * they will come through the model seam with their own `tinted` styling; until then, matching the
 * house style is the honest look.
 */

export class FaunaFactory {
  /** Materials are shared across every view this factory makes and disposed with it. */
  private readonly materials: THREE.Material[] = [];

  build(definition: FaunaDefinition, instance: FaunaInstance): THREE.Object3D {
    const pose = poseOf(definition, instance);
    const group = new THREE.Group();
    group.name = 'fauna';
    group.userData.faunaInstanceId = instance.id;

    const parts = buildFaunaGeometry(definition, pose, { attachment: instance.attachment });
    if (parts.body) {
      group.add(this.mesh(parts.body, definition.rendering.colors.body, 'body'));
    }
    if (parts.accent) {
      group.add(this.mesh(parts.accent, definition.rendering.colors.accent, 'accent'));
    }

    this.applyTransform(group, definition, instance);
    return group;
  }

  /**
   * Position, face, and scale a view.
   *
   * `heightOffsetMeters` is added to the sampled ground rather than replacing it, which is what makes
   * one field serve three attachments: zero for a creature on the ground, the perch's height for one
   * on a branch, the record's hover height for one in the air. The world supplies the ground; this
   * only knows the offset above it.
   */
  applyTransform(object: THREE.Object3D, definition: FaunaDefinition, instance: FaunaInstance): void {
    object.position.set(instance.position.x, instance.position.y, instance.position.z);
    // The record's yaw correction and the instance's own heading. Assets are expected to face +Z;
    // the correction exists for the ones that do not, exactly as it does for props.
    object.rotation.set(
      0,
      instance.rotationY + THREE.MathUtils.degToRad(definition.rendering.yawOffsetDegrees),
      0,
    );
    const scale = instanceScale(instance);
    object.scale.setScalar(scale);
    object.visible = !instance.hidden;
  }

  private mesh(geometry: THREE.BufferGeometry, color: string, name: string): THREE.Mesh {
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      roughness: 0.78,
      metalness: 0,
      flatShading: true,
      // `MeshBuilder` bakes a shading term into the colour attribute; without this the buffer is
      // built and ignored, and the silhouettes come out uniformly lit.
      vertexColors: true,
      // Wings are single-sided planes. Without this a butterfly disappears from one side.
      side: THREE.DoubleSide,
    });
    this.materials.push(material);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.castShadow = true;
    // Deliberately not receiving: these are small convex bodies, and self-shadowing a 20 mm bee
    // costs a shadow-map lookup to produce nothing a viewer can see.
    mesh.receiveShadow = false;
    return mesh;
  }

  /** Dispose one view's geometries. Materials are shared and freed by {@link dispose}. */
  disposeView(object: THREE.Object3D): void {
    object.traverse((node) => {
      (node as Partial<THREE.Mesh>).geometry?.dispose();
    });
  }

  /** Release the shared materials. Called when the world goes away. */
  dispose(): void {
    for (const material of this.materials) material.dispose();
    this.materials.length = 0;
  }
}

/** The pose an instance is in, falling back to the record's default if its `poseId` went stale. */
export function poseOf(definition: FaunaDefinition, instance: FaunaInstance): FaunaPose {
  return definition.poses.find((pose) => pose.id === instance.poseId) ?? defaultPose(definition);
}
